import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errMsg } from '@/services/staffAttendanceApi';
import { downloadCsv } from '@/services/transportApi';

export const money = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
export const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const num = (v) => <span className="tabular-nums">{v}</span>;
const COLS = [['days', 'Days'], ['truck_days', 'Truck days'], ['unload_hours', 'Unload hrs'], ['piece_days', 'Piece days'],
  ['hours', 'Hours'], ['review', 'HR to decide'], ['missed', 'Missed'], ['pending', 'Waiting']];
const warn = (k, v) => (v && (k === 'missed' || k === 'review') ? 'text-amber-600 font-semibold' : k === 'days' ? 'font-semibold' : '');
const Cells = ({ x, id }) => COLS.map(([k]) => <TableCell key={k} className={warn(k, x[k])} data-testid={id ? `${id}-${k}` : undefined}>{num(x[k])}</TableCell>);

const SectionTable = ({ a, id }) => (
  <Table data-testid={`payroll-sections-${id}`}>
    <TableHeader><TableRow>{['Section', 'Workers', ...COLS.map(([, l]) => l), 'Amount'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
    <TableBody>
      {a.sections.map((s) => (
        <TableRow key={s.section} data-testid={`payroll-sec-row-${id}-${slug(s.section)}`}>
          <TableCell className="font-medium">{s.section}</TableCell><TableCell>{num(s.workers)}</TableCell>
          <Cells x={s} />
          <TableCell className="font-semibold">{money(s.amount)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const chip = (c) => [`${c.days}d`, c.truck_days ? `T ${c.truck_days}d` : '', c.unload_hours ? `U ${c.unload_hours}h` : '', c.piece_days ? `P ${c.piece_days}` : '', `${c.hours}h`]
  .filter(Boolean).join(' · ');

const WorkerRow = ({ s }) => (
  <TableRow data-testid={`payroll-worker-${s.staff_id}`} className={s.roll_status === 'left' ? 'text-gray-500' : ''}>
    <TableCell className="font-mono">{s.staff_id}</TableCell>
    <TableCell className="font-medium">{s.staff_name}{s.roll_status === 'left' && <Badge className="ml-2 bg-rose-100 text-rose-700 hover:bg-rose-100">Left</Badge>}</TableCell>
    <TableCell>{s.home_section || '—'}</TableCell>
    <TableCell>
      <div className="flex flex-wrap gap-1">
        {s.sections.map((c) => (
          <span key={c.section} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs" data-testid={`payroll-worker-sec-${s.staff_id}-${slug(c.section)}`}>
            {c.section} · {chip(c)}{c.review ? <b className="text-amber-600"> · {c.review} HR?</b> : null}{c.missed ? <b className="text-amber-600"> · {c.missed}M</b> : null}
          </span>
        ))}
      </div>
    </TableCell>
    <Cells x={s} id={`payroll-worker-${s.staff_id}`} />
    <TableCell className="font-semibold" data-testid={`payroll-worker-amount-${s.staff_id}`}>{money(s.amount)}</TableCell>
  </TableRow>
);

const rateLine = (a) => [['Day', a.day_rate, '/ day'], ['Truck Loadman', a.truck_rate, '/ day'], ['Unloading', a.hourly_rate, '/ hr']]
  .map(([l, v, u]) => `${l} ${v === null || v === undefined ? '—' : `${money(v)} ${u}`}`).join(' · ');

/** One agency: totals, PDF statement, section-wise table and (expandable) worker-wise table. */
export const PayrollAgencyCard = ({ a, dateFrom, dateTo, section, expand = false }) => {
  const id = slug(a.agency);
  const [manualOpen, setOpen] = useState(false);
  const open = manualOpen || expand;
  const [busy, setBusy] = useState(false);
  const pdf = () => {
    setBusy(true);
    const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, agency: a.agency, ...(section ? { section } : {}) });
    downloadCsv(`/staff-attendance/contract-payroll/pdf?${q}`, `contract_payroll_${id}_${dateFrom}_to_${dateTo}.pdf`)
      .catch((e) => toast.error(errMsg(e, 'PDF failed'))).finally(() => setBusy(false));
  };
  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid={`payroll-agency-${id}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 bg-slate-50 border-b">
        <div>
          <div className="text-lg font-bold" data-testid={`payroll-agency-name-${id}`}>{a.agency}</div>
          <div className="text-xs text-gray-500" data-testid={`payroll-agency-rate-${id}`}>{rateLine(a)}</div>
        </div>
        {[['Workers', a.workers, 'workers'], ['Days', a.days, 'days'], ['Truck days', a.truck_days, 'truck_days'], ['Unload hrs', a.unload_hours, 'unload_hours'],
          ['Hours', a.hours, 'hours'], ['HR to decide', a.review, 'review'], ['Amount', money(a.amount), 'amount']].map(([l, v, k]) => (
          <div key={k}><div className="text-[10px] uppercase text-gray-500">{l}</div><div className={`font-bold tabular-nums ${k === 'review' && v ? 'text-amber-600' : ''}`} data-testid={`payroll-agency-${k}-${id}`}>{v}</div></div>
        ))}
        <Button size="sm" onClick={pdf} disabled={busy} className="ml-auto bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid={`payroll-pdf-${id}`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}PDF statement
        </Button>
      </div>
      <CardContent className="p-0 overflow-x-auto">
        <SectionTable a={a} id={id} />
        <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-center gap-1 py-2 text-sm text-[#2D5F4F] hover:bg-slate-50 border-t" data-testid={`payroll-toggle-workers-${id}`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}{open ? 'Hide' : 'Show'} workers ({a.workers})
        </button>
        {open && (
          <Table data-testid={`payroll-workers-${id}`}>
            <TableHeader><TableRow>{['Staff ID', 'Name', 'Home section', 'Worked in', ...COLS.map(([, l]) => l), 'Amount'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{a.staff.map((s) => <WorkerRow key={s.staff_id} s={s} />)}</TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default PayrollAgencyCard;
